import { apiClient, ApiResponse } from './api-client'
import type { PublicMenu, PublicMenuCategory, PublicMenuItem } from '../models/menu'

export interface MenuFilters {
  categoryId?: string
  minPrice?: number
  maxPrice?: number
  search?: string
}

export class PublicMenuService {
  /**
   * Get public menu for a business by slug
   */
  async getPublicMenu(slug: string, filters?: MenuFilters): Promise<PublicMenu> {
    const response = await apiClient.get<ApiResponse<{ menu: PublicMenu }>>(
      `/menus/public/${slug}`,
      { params: filters }
    )
    if (response.data.success && response.data.data) {
      return response.data.data.menu
    }
    throw new Error(response.data.error?.message || 'Failed to get menu')
  }

  /**
   * Get public category with items
   */
  async getPublicCategory(businessId: string, categoryId: string, filters?: MenuFilters): Promise<PublicMenuCategory> {
    const response = await apiClient.get<ApiResponse<{ category: PublicMenuCategory }>>(
      `/menus/public/${businessId}/category/${categoryId}`,
      { params: filters }
    )
    if (response.data.success && response.data.data) {
      return response.data.data.category
    }
    throw new Error(response.data.error?.message || 'Failed to get category')
  }

  /**
   * Search menu items by business slug
   */
  async searchItems(slug: string, searchTerm: string): Promise<PublicMenuItem[]> {
    const response = await apiClient.get<ApiResponse<{ items: PublicMenuItem[] }>>(
      `/menus/public/${slug}/search`,
      { params: { q: searchTerm } }
    )
    if (response.data.success && response.data.data) {
      return response.data.data.items || []
    }
    throw new Error(response.data.error?.message || 'Search failed')
  }

  /**
   * Get items in price range
   */
  async getItemsInPriceRange(businessId: string, minPrice: number, maxPrice: number): Promise<PublicMenuItem[]> {
    const response = await apiClient.get<ApiResponse<{ items: PublicMenuItem[] }>>(
      `/menus/public/${businessId}/price-range`,
      { params: { minPrice, maxPrice } }
    )
    if (response.data.success && response.data.data) {
      return response.data.data.items || []
    }
    throw new Error(response.data.error?.message || 'Failed to get items')
  }

  /**
   * Get menu statistics
   */
  async getMenuStats(businessId: string): Promise<any> {
    const response = await apiClient.get<ApiResponse>(`/menus/public/${businessId}/stats`)
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    throw new Error(response.data.error?.message || 'Failed to get stats')
  }
}

// Export singleton instance
export const publicMenuService = new PublicMenuService()

